import { ObjectFactory } from "@/models/objects/ObjectFactory";
import {
	type ICrossSection,
	type ICrossSectionSettings,
	type IObject,
	ObjectType,
} from "@/models/objects/types";
import type { DistanceUnit } from "@/models/siteUnit.types";
import * as cesiumCommon from "@/utils/cesium-common";
import { getCookie } from "@/utils/getCookie";
import * as lockUtils from "@/utils/lock-utils";
import axios, { type AxiosBasicCredentials } from "axios";
import { saveAs } from "file-saver";
import { watch } from "vue";
import { i18n } from "@/i18n.js";

//It acts as object Manager
// Object Type : Road, Flatground, Trench, SolidMesh

/** Vuex action context surface used by this module's actions. */
interface IObjectActionContext {
	state: IObjectState;
	getters: Record<string, unknown>;
	commit: (
		type: string,
		payload?: unknown,
		options?: { root?: boolean },
	) => void;
	dispatch: (
		type: string,
		payload?: unknown,
		options?: { root?: boolean },
	) => Promise<unknown>;
	rootState: IObjectRootState;
}

/** Root state slices this module reads; other keys stay `unknown`. */
interface IObjectRootState extends Record<string, unknown> {
	authObject: AxiosBasicCredentials;
	drawing2dImage?: {
		imageUnderEdit?: unknown;
		isTwoPointAlignment?: boolean;
	};
	lock: { lockInfo: { objId?: string; failed?: boolean } | null };
}

type EditFormValiditySource =
	| "manual-transform"
	| "single-point-transform"
	| "manual-detail"
	| "single-point-detail"
	| "double-point-detail";

export interface IObjectState {
	objects: IObject[];
	objectUnderEdit?: IObject;
	objectUpdateDialog: {
		visible: boolean;
		resolve: ((value: string | null) => void) | null;
	};
	isEditFormValid: boolean;
	editFormValidityBySource: Partial<Record<EditFormValiditySource, boolean>>;
}

const state: IObjectState = {
	objects: [],
	objectUnderEdit: undefined,
	objectUpdateDialog: {
		visible: false,
		resolve: null as ((value: string | null) => void) | null,
	},
	isEditFormValid: true,
	editFormValidityBySource: {},
};

const mutations = {
	setObjects(state, objects: IObject[]) {
		state.objects = [...objects];
	},
	addObject(state: IObjectState, object: IObject) {
		// Duplicate guard: a solid mesh object is first created in the Viewer
		// (with a UUID) and registered here only after the user confirms creation
		// (onFinish callback). Temporary/preview meshes are held in component-local
		// state (pickedTempSolidMeshId) and are never added to the store until
		// confirmed, so duplicates should not occur in normal flows.
		//
		// The one legitimate re-entry path is applyPreviewParams, which calls
		// loadSolidMeshJSON for a mesh that is already registered when refreshing
		// a preview. The guard prevents a double-entry in that case.
		if (!state.objects.some((obj) => obj.id === object.id)) {
			state.objects = [...state.objects, object];
		}
	},
	removeObject(state: IObjectState, objectId: string) {
		state.objects = state.objects.filter((obj) => obj.id !== objectId);
	},
	updateObject(state: IObjectState, updatedObject: IObject) {
		const existing = state.objects.find((obj) => obj.id === updatedObject.id);
		if (existing) {
			Object.assign(existing, updatedObject);
		}
	},
	setObjectUnderEdit(state, object: IObject) {
		state.objectUnderEdit = object;
		state.isEditFormValid = true;
		state.editFormValidityBySource = {};
	},
	clearObjectUnderEdit(state) {
		state.objectUnderEdit = undefined;
		state.isEditFormValid = true;
		state.editFormValidityBySource = {};
	},
	setEditFormValid(state: IObjectState, isValid: boolean) {
		state.isEditFormValid = isValid;
	},
	setEditFormValidBySource(
		state: IObjectState,
		payload: { source: EditFormValiditySource; isValid: boolean },
	) {
		state.editFormValidityBySource[payload.source] = payload.isValid;
		const values = Object.values(state.editFormValidityBySource);
		state.isEditFormValid = values.length === 0 ? true : values.every(Boolean);
	},
};

const actions = {
	async get_obj({ state, commit, rootState }, json_id) {
		try {
			const response = await axios.get(
				`${import.meta.env.VITE_API_BASE}/obj/${json_id}`,
				{
					auth: rootState.authObject,
				},
			);
			const data = response.data;
			const obj = await ObjectFactory.createObject(data);
			if (obj) {
				const currentObj = state.objects.find((entity) => entity.id === obj.id);
				// check if the object is changed
				if (currentObj) {
					if (currentObj.md5 !== obj.md5) {
						await currentObj.update(obj); // update the object
						commit("updateObject", obj);
					}
				} else {
					// add the object to the list
					commit("addObject", obj);
				}
			}
		} catch (error: unknown) {
			console.error(error);
		}
	},
	async get_obj_list(
		{ state, commit, rootState }: IObjectActionContext,
		site_id: string,
	) {
		// ToDo: sync object list from server
		// if new object has been added, add it to the list
		// if object has been deleted, remove it from the list and also from viewer for safely
		// if object is being edited, it cannot be deleted
		// if object has been updated, update it in the list and if it is loaded in the viewer, update it in the viewer

		// Deleted objects will be silently removed from the list if the app is reloaded

		try {
			const objects: IObject[] = [];
			const response = await axios.get(
				`${import.meta.env.VITE_API_BASE}/obj/?siteId=${site_id}`,
				{
					auth: rootState.authObject,
				},
			);
			for (const data of response.data) {
				const obj = await ObjectFactory.createObject(data);
				if (obj) {
					// check if the object is already in the list
					const currentObj = state.objects.find(
						(entity) => entity.id === obj.id,
					);
					// check if the object is changed
					if (currentObj) {
						if (currentObj.md5 !== obj.md5) {
							// update the object
							await currentObj.update(obj, obj.isShow);
						}
						objects.push(currentObj);
					} else {
						// add the object to the list
						objects.push(obj);
					}
				}
			}
			commit("setObjects", objects);
		} catch (error: unknown) {
			console.error(error);
		}
	},
	async deleteObject(
		{ state, commit, dispatch, rootState, rootGetters },
		{ siteId, obj },
	) {
		const lockedObject = await lockUtils.checkLockStatus(siteId, obj.id);
		if (lockedObject.locked_by) {
			throw new Error(`LOCKED:${lockedObject.locked_by}`);
		}

		const params = {
			siteId: siteId,
			cid: obj.id,
		};
		try {
			// Delete cross-section from DB if exists
			if (obj.crossSection?.cross_section_id) {
				try {
					await dispatch(
						"lineWorksCrossSection/deleteCrossSection",
						{ siteId, crossSectionId: obj.crossSection.cross_section_id },
						{ root: true },
					);
				} catch (error: unknown) {
					console.error("Failed to delete cross-section:", error);
				}
			}

			// Delete the object itself
			await axios.delete(`${import.meta.env.VITE_API_BASE}/obj/`, {
				params,
				auth: rootState.authObject,
			});

			const objects = state.objects.filter((item) => item.id !== obj.id);
			commit("setObjects", objects);
			console.log("delete success");
		} catch (error: unknown) {
			console.error("delete failed:", error);
			throw error;
		}
	},

	async createObj(
		{ state, commit, rootState },
		{ siteId, obj }: { siteId: string; obj: IObject },
	) {
		const propsJSON = await obj.toJSON();
		try {
			await axios.post(
				`${import.meta.env.VITE_API_BASE}/obj/${siteId}`,
				{
					siteId,
					objJSON: {},
					objProps: propsJSON,
					objConnectionJSON: null,
				},
				{
					auth: rootState.authObject,
				},
			);
		} catch (e: unknown) {
			console.error("CreateObj failed:", e);
			throw e;
		}
		// Only add if not already in store
		const exists = state.objects.some((o) => o.id === obj.id);
		if (!exists) {
			commit("addObject", obj);
		}
	},

	async updateObj({ state, rootState }, { props, objConnectionJSON }) {
		const obj: IObject = state.objects.find((obj) => obj.id === props.id);
		if (!obj) {
			throw new Error(`Object not found with id: ${props.id}`);
		}
		try {
			console.log("updateObj put request");
			const propsJSON = await obj.toJSON();
			await axios.put(
				`${import.meta.env.VITE_API_BASE}/obj`,
				{
					siteId: rootState.site.site_uuid,
					objJSON: {},
					objProps: propsJSON,
					objConnectionJSON: objConnectionJSON,
				},
				{
					auth: rootState.authObject,
				},
			);
		} catch (e: unknown) {
			console.error(e);
			throw e;
		}
	},

	async updateName(
		{ state, rootState, dispatch, commit, rootGetters },
		{ obj, objProps },
	) {
		try {
			const res = await axios.put(
				`${import.meta.env.VITE_API_BASE}/obj`,
				{
					siteId: rootState.site.site_uuid,
					objJSON: {},
					objProps: objProps,
					objConnectionJSON: null,
				},
				{
					auth: rootState.authObject,
				},
			);

			if (obj.crossSection?.id) {
				try {
					await axios.put(
						`${import.meta.env.VITE_API_BASE}/crossSection/name/${rootState.site.site_uuid}/${obj.crossSection.id}`,
						{ name: obj.name },
						{ auth: rootState.authObject },
					);
					obj.crossSection.name = obj.name;
				} catch (err: unknown) {
					console.error("Failed to update cross-section name:", err);
				}
			}

			return res.status;
		} catch (e: unknown) {
			console.error(e);
			throw e;
		}
	},
	async startEdit(
		{ state, getters, commit, dispatch, rootState }: IObjectActionContext,
		object: IObject,
	) {
		//if there is an object or image under edit, or 2-point alignment is in progress, end it first
		if (
			state.objectUnderEdit ||
			rootState.drawing2dImage?.imageUnderEdit ||
			rootState.drawing2dImage?.isTwoPointAlignment
		) {
			const ended = await dispatch("inProgressModeManager/endEdit", null, {
				root: true,
			});
			if (!ended) return;
		}
		lockUtils.lock(object.id);
		// wait for lock
		const lockSuccess = await new Promise<boolean>((resolve) => {
			const unwatch = watch(
				() => rootState.lock.lockInfo,
				(newValue) => {
					// When the edit status becomes false, stop waiting
					if (newValue?.objId === object.id) {
						unwatch();
						resolve(!newValue.failed);
					}
				},
			);
		});

		// If lock fails, abort processing
		if (!lockSuccess) {
			return;
		}

		// After lock succeeds, switch to edit mode
		const editStarted = await dispatch(
			"inProgressModeManager/startEdit",
			{ objectType: object.type, objectId: object.id },
			{ root: true },
		);
		if (!editStarted) {
			lockUtils.releaseLock();
			return;
		}

		try {
			await object.startEdit();
			dispatch("lock/keepLock", null, { root: true });
			commit("setObjectUnderEdit", object);
		} catch (e: unknown) {
			console.error(e);
			lockUtils.releaseLock();
			await dispatch("inProgressModeManager/endEdit", null, {
				root: true,
			});
		}
	},
	async endEdit({ state, commit, dispatch, rootState }: IObjectActionContext) {
		// end it and unlock it
		if (state.objectUnderEdit) {
			// Check validation before ending edit
			if (!state.isEditFormValid) {
				commit(
					"set_snackbar",
					{
						text: i18n.global.t("CANNOT_FINISH_EDITING_INVALID_VALUES"),
						color: "rgba(153, 0, 0, 0.72)",
						timeout: -1,
					},
					{ root: true },
				);
				return false;
			}

			const objectToClose = state.objectUnderEdit;
			await objectToClose.endEdit();

			lockUtils.releaseLock();
			// wait for release lock
			await new Promise((resolve, reject) => {
				const unwatch = watch(
					() => rootState.lock.lockInfo,
					(newValue) => {
						// When the edit status becomes false, stop waiting
						if (!newValue) {
							unwatch();
							resolve(true);
						}
					},
				);
			});
			objectToClose.isOpen = false;
			commit("clearObjectUnderEdit");
		}
	},
	openObjectUpdateDialog({ state }): Promise<string | null> {
		return new Promise((resolve) => {
			state.objectUpdateDialog.visible = true;
			state.objectUpdateDialog.resolve = resolve;
		});
	},
	closeObjectUpdateDialog({ state }, selectedValue: string | null) {
		state.objectUpdateDialog.visible = false;
		state.objectUpdateDialog.resolve?.(selectedValue);
		state.objectUpdateDialog.resolve = null;
	},

	async duplicateObject(
		{ state, commit, dispatch },
		{ siteId, obj }: { siteId: string; obj: IObject },
	) {
		// Check if locked by another user
		const lockedObject = await lockUtils.checkLockStatus(siteId, obj.id);
		if (lockedObject.locked_by) {
			throw new Error(`LOCKED:${lockedObject.locked_by}`);
		}

		// retain the original object's cross-section settings
		const originalCrossSection: ICrossSectionSettings = (obj as any)
			.crossSection;

		const res = await cesiumCommon.duplicateObject(obj, siteId);
		if (res?.json_name) {
			await dispatch("get_obj", res.json_name);

			if (originalCrossSection) {
				const duplicatedObj = state.objects.find((o) => o.id === res.json_name);
				if (duplicatedObj) {
					try {
						const savedCrossSection: ICrossSection = await dispatch(
							"lineWorksCrossSection/saveCrossSectionOnCreate",
							{
								object: duplicatedObj,
								siteId: siteId,
								crossSectionParams: {
									leftWidth: originalCrossSection.leftWidth,
									rightWidth: originalCrossSection.rightWidth,
									interpolationPitch: originalCrossSection.interpolationPitch,
									samplingSize: originalCrossSection.samplingSize,
								},
							},
							{ root: true },
						);
						if (savedCrossSection) {
							const updatedDuplicatedObj = {
								...duplicatedObj,
								crossSection: {
									id: savedCrossSection.cross_section_id,
									leftWidth:
										savedCrossSection.left_side_width ??
										originalCrossSection.leftWidth,
									rightWidth:
										savedCrossSection.right_side_width ??
										originalCrossSection.rightWidth,
									interpolationPitch:
										savedCrossSection.station_interval ??
										originalCrossSection.interpolationPitch,
									samplingSize:
										savedCrossSection.sampling_size ??
										originalCrossSection.samplingSize,
								},
							};
							commit("updateObject", updatedDuplicatedObj);
						}
					} catch (error: unknown) {
						console.error("Failed to duplicate cross-section settings:", error);
					}
				}
			}
		}
	},

	async exportIfc(
		_context,
		{
			siteId,
			geometry,
			name,
			filename,
			distanceUnit,
			metersPerUnit,
		}: {
			siteId: string;
			// Vertices are already converted to distanceUnit by the caller.
			geometry: { vertices: number[]; indices: number[] };
			name: string;
			filename: string;
			distanceUnit: DistanceUnit;
			metersPerUnit: number;
		},
	) {
		try {
			const res = await axios.post(
				`${import.meta.env.VITE_API_V2_BASE}/${siteId}/object/ifc`,
				{
					vertices: geometry.vertices,
					indices: geometry.indices,
					name,
					distanceUnit,
					metersPerUnit,
				},
				{
					responseType: "blob",
					headers: {
						Authorization: `Bearer ${getCookie("access_token")}`,
					},
				},
			);
			saveAs(res.data, filename);
		} catch (e: unknown) {
			console.error("ExportIfc failed:", e);
			throw e;
		}
	},
};

const getters = {
	objects: (state) => state.objects,
	isEditFormValid: (state: IObjectState) => state.isEditFormValid,
};

export default {
	namespaced: true,
	state,
	mutations,
	actions,
	getters,
};
